import React, {useEffect, useRef, useState} from 'react';
import {DataGrid, GridColDef, GridPaginationModel, GridSortModel} from '@mui/x-data-grid';
import {fetchAdminFavoritesCsv, fetchAdminFavoritesPage} from '../../api/endpoints';
import {DateRangeMenu, SingleSelectMenu} from '../../utils/DropwdownUtils/DropdownUtils';
import {isRight} from 'fp-ts/lib/Either';
import AdminFavoriteDto from '../../interfaces/AdminFavoriteDto';
import {formatDateObjToMonthAndDay, formatDateToFullDate} from '../../utils/DateUtils/DateUtils';


const TYPE_OPTIONS: string[] = ['Performer', 'Venue'];

const typeLabelToParam = (label: string): 'PERFORMER' | 'VENUE' | undefined => {
    if (label === 'Performer') return 'PERFORMER';
    if (label === 'Venue') return 'VENUE';
    return undefined;
};

const AdminFavorites: React.FC = () => {
    const datePickerRef = useRef<HTMLDivElement>(null);
    const typeRef = useRef<HTMLDivElement>(null);

    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
    const [activeDatePicker, setActiveDatePicker] = useState(false);

    const [selectedType, setSelectedType] = useState<string>('Performer');
    const [activeTypePicker, setActiveTypePicker] = useState(false);

    const [filterQuery, setFilterQuery] = useState('');
    const [lastSearched, setLastSearched] = useState('');

    const [rows, setRows] = useState<AdminFavoriteDto[]>([]);
    const [totalRows, setTotalRows] = useState<number>(0);
    const [loading, setLoading] = useState(true);

    const [sortModel, setSortModel] = useState<GridSortModel>([
        {field: 'updatedAt', sort: 'desc'}
    ]);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 10,
    });

    const typeFiltered = selectedType !== '';

    const columns: GridColDef[] = [
        {field: 'userFirstName', headerName: 'First Name', width: 120, disableColumnMenu: true},
        {field: 'userLastName', headerName: 'Last Name', width: 120, disableColumnMenu: true},
        {field: 'userEmail', headerName: 'Email', width: 220, disableColumnMenu: true},
        {field: 'favoriteType', headerName: 'Favorite Type', width: 130, disableColumnMenu: true},
        {field: 'favoriteName', headerName: 'Favorite Name', width: 220, disableColumnMenu: true},
        {
            field: 'updatedAt',
            headerName: 'Date Saved',
            width: 180,
            disableColumnMenu: true,
            renderCell: (params) => formatDateToFullDate(params.value)
        },
        {field: 'favoriteState', headerName: 'Favorite State', width: 130, disableColumnMenu: true},
    ];

    const fetchData = async (
        type: string,
        dates: [Date | null, Date | null],
        search: string,
        pagination: GridPaginationModel,
        sort: GridSortModel,
    ) => {
        setLoading(true);
        const sortBy = sort[0]?.field || 'updatedAt';
        const order = sort[0]?.sort || 'desc';
        const responseEither = await fetchAdminFavoritesPage(
            pagination.page,
            pagination.pageSize,
            typeLabelToParam(type),
            dates[0],
            dates[1],
            search || undefined,
            sortBy,
            order,
        );
        if (isRight(responseEither)) {
            const {content, total} = responseEither.right;
            setRows(content as AdminFavoriteDto[]);
            setTotalRows(total);
        } else {
            console.error(responseEither.left);
        }
        setLoading(false);
    };

    useEffect(() => {
        const handleClickOutsideDatePicker = (e: MouseEvent) => {
            if (datePickerRef.current && e.target instanceof Node && !datePickerRef.current.contains(e.target)) {
                setActiveDatePicker(false);
            }
        };
        const handleClickOutsideTypePicker = (e: MouseEvent) => {
            if (typeRef.current && e.target instanceof Node && !typeRef.current.contains(e.target)) {
                setActiveTypePicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutsideDatePicker);
        document.addEventListener('mousedown', handleClickOutsideTypePicker);
        return () => {
            document.removeEventListener('mousedown', handleClickOutsideDatePicker);
            document.removeEventListener('mousedown', handleClickOutsideTypePicker);
        };
    }, []);

    useEffect(() => {
        fetchData(selectedType, dateRange, lastSearched, paginationModel, sortModel);
    }, [paginationModel, sortModel, selectedType]);

    const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilterQuery(e.target.value);
    };

    const handleFilterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        fetchData(selectedType, dateRange, filterQuery, paginationModel, sortModel);
        setLastSearched(filterQuery);
    };

    const submitExportCsv = async () => {
        if (!dateRange[0] || !dateRange[1]) return;
        const responseEither = await fetchAdminFavoritesCsv(dateRange[0], dateRange[1]);
        if (isRight(responseEither)) {
            try {
                const csvString = responseEither.right;
                const blob = new Blob([csvString], {type: 'text/csv'});
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'favorites.csv';
                link.click();
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Download failed:', error);
            }
        } else {
            console.error(responseEither.left);
        }
    };

    const dateFiltered = dateRange[1] !== null;
    const dateRangeString = (dateRange[0] && dateRange[1])
        ? formatDateObjToMonthAndDay(dateRange[0]) + ' - ' + formatDateObjToMonthAndDay(dateRange[1])
        : '';

    return (
        <div className='card card-top' style={{minHeight: 500, width: '100%'}}>
            <div className='row'>
                <div className='left'>
                    <div className='text-l'>Favorites</div>
                </div>
                <div className='relative' ref={datePickerRef}>
                    <button className={`admin-button ${dateFiltered && 'admin-filtered'}`}
                            onClick={() => setActiveDatePicker(!activeDatePicker)}>
                        {dateFiltered ? dateRangeString : 'Select Date Range to Export'}
                    </button>
                    {activeDatePicker &&
                        <DateRangeMenu
                            startDate={dateRange[0]}
                            endDate={dateRange[1]}
                            onChange={(dates) => setDateRange(dates)}
                            onClose={() => setActiveDatePicker(false)}
                        />
                    }
                </div>
                {(dateRange[0] && dateRange[1]) &&
                    <>
                        <div className='gap-s'/>
                        <button type='button' className='admin-delete-button' onClick={submitExportCsv}>Export CSV</button>
                    </>
                }
            </div>
            <div className='card-item'>
                <form onSubmit={handleFilterSubmit} className='card-item'>
                    <div className='row'>
                        <input type='text' value={filterQuery} onChange={handleQueryChange}
                               placeholder='Type to filter by performer or venue name'
                               className='admin-search'
                        />
                        <button type='submit' className='admin-action-button'>Filter</button>
                        <div className='gap-s'/>
                        <div className='relative' ref={typeRef}>
                            <button className={`admin-button ${typeFiltered && 'admin-filtered'}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setActiveTypePicker(!activeTypePicker);
                                    }}
                            >
                                {typeFiltered ? selectedType : 'Favorite Type'}
                            </button>
                            {activeTypePicker &&
                                <SingleSelectMenu
                                    options={TYPE_OPTIONS}
                                    selectedOption={selectedType}
                                    setSelectedOption={setSelectedType}
                                    closeMenu={() => setActiveTypePicker(false)}
                                    mustSelect={false}
                                    rightAligned={true}
                                />
                            }
                        </div>
                        <div className='gap-s'/>
                        <button className='admin-button' style={{padding: '4px 12px'}}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setFilterQuery('');
                                    setSelectedType('');
                                    setLastSearched('');
                                    fetchData('', dateRange, '', paginationModel, sortModel);
                                }}
                        >
                            ↻
                        </button>
                    </div>
                </form>
            </div>
            {loading
                ? <div className='loading-spinner'/>
                : <DataGrid
                    rows={rows}
                    columns={columns}
                    paginationMode='server'
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    sortingMode='server'
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    rowCount={totalRows}
                    pageSizeOptions={[5, 10, 20]}
                    className='admin-data-grid'
                    disableRowSelectionOnClick
                />
            }
        </div>
    );
};

export default AdminFavorites;
